import { Reservation } from 'src/types/reservation'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import {
  regItemReservationsKey,
  useUnreserveReservation,
} from 'src/routes/(registry)/api/reservations'
import {
  invalidateRegItemReservationsQuery,
  invalidateRegItemsQuery,
} from 'src/routes/(registry)/list/[slug]/utils/registryHelpers'
import { RegItem } from 'src/types/regItem'
import useUnmarkAsPurchasedEligible from '../useUnmarkAsPurchasedEligible/useUnmarkAsPurchasedEligible'

const optimisticallyRemoveReservation = (
  queryClient: QueryClient,
  reservation: Reservation
) => {
  queryClient.setQueryData(
    regItemReservationsKey(reservation.regItemId),
    (data: Reservation[] = []) =>
      data.filter((res) => res.id !== reservation.id)
  )
}

interface UseUnmarkAsPurchasedProps {
  reservation: Reservation
  onSuccess?: (reservation: Reservation) => void
}

const getUnmarkAsPurchasedCtaText = (regItem: RegItem) => {
  if (regItem.isCrowdfund) {
    return 'Delete contribution'
  }

  if (regItem.isFavor) {
    return 'Delete favor'
  }

  return 'Unmark as purchased'
}

const useUnmarkAsPurchased = ({
  reservation,
  onSuccess,
}: UseUnmarkAsPurchasedProps) => {
  const queryClient = useQueryClient()
  const isUnmarkEligible = useUnmarkAsPurchasedEligible(reservation)
  const ctaText = getUnmarkAsPurchasedCtaText(reservation.regItem)

  const handleSuccess = ({
    reservation: res,
  }: {
    reservation: Reservation
  }) => {
    optimisticallyRemoveReservation(queryClient, res)
    invalidateRegItemReservationsQuery(queryClient, res.regItemId)
    invalidateRegItemsQuery({ queryClient, registryId: res.registryId })
    onSuccess?.(res)
  }

  const { isLoading, mutate } = useUnreserveReservation({
    reservationToken: reservation.token,
    onSuccess: handleSuccess,
  })

  return {
    ctaText,
    isUnmarkEligible,
    isLoading,
    mutate,
  }
}

export default useUnmarkAsPurchased
